import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ContinuousImprovementCardProps {
  title: string;
  description: string;
  status: 'completed' | 'in-progress' | 'planned';
  impact: string;
  startDate: string;
  completionDate: string;
  team: string;
  image: string;
  alt: string;
}

export default function ContinuousImprovementCard({
  title,
  description,
  status,
  impact,
  startDate,
  completionDate,
  team,
  image,
  alt
}: ContinuousImprovementCardProps) {
  const statusConfig = {
    completed: { color: 'bg-success text-success-foreground', label: 'Completed', icon: 'CheckCircleIcon' },
    'in-progress': { color: 'bg-warning text-warning-foreground', label: 'In Progress', icon: 'ClockIcon' },
    planned: { color: 'bg-muted text-muted-foreground', label: 'Planned', icon: 'CalendarIcon' }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-all duration-base">
      <div className="relative h-48 bg-muted overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover"
        />
        <div className={`absolute top-4 left-4 px-3 py-1 rounded-md text-xs font-cta font-bold ${statusConfig[status].color} flex items-center space-x-1`}>
          <Icon name={statusConfig[status].icon as any} size={14} variant="solid" />
          <span>{statusConfig[status].label}</span>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-headline font-bold text-foreground mb-2">{title}</h3>
        <p className="text-sm font-body text-muted-foreground mb-4">{description}</p>
        
        <div className="space-y-3 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="ChartBarIcon" size={18} variant="outline" className="text-primary" />
            <div>
              <p className="text-xs font-body text-muted-foreground">Impact</p>
              <p className="text-sm font-body font-semibold text-foreground">{impact}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon name="UserGroupIcon" size={18} variant="outline" className="text-primary" />
            <div>
              <p className="text-xs font-body text-muted-foreground">Team</p>
              <p className="text-sm font-body font-semibold text-foreground">{team}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2">
              <Icon name="CalendarIcon" size={18} variant="outline" className="text-primary" />
              <div>
                <p className="text-xs font-body text-muted-foreground">Start Date</p>
                <p className="text-sm font-body font-semibold text-foreground">{startDate}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Icon name="FlagIcon" size={18} variant="outline" className="text-primary" />
              <div>
                <p className="text-xs font-body text-muted-foreground">Completion</p>
                <p className="text-sm font-body font-semibold text-foreground">{completionDate}</p>
              </div>
            </div>
          </div>
        </div>
        
        <button className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-cta font-bold hover:opacity-90 transition-all duration-base flex items-center justify-center space-x-2">
          <Icon name="DocumentTextIcon" size={16} variant="outline" />
          <span>View Project Details</span>
        </button>
      </div>
    </div>
  );
}